import { getCountries, getCountryCallingCode } from "libphonenumber-js";

export const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
export const COUNTRY_LIST = getCountries()
  .map((code) => ({
    code,
    name: regionNames.of(code) || code,
    dialCode: `+${getCountryCallingCode(code)}`,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export const EMPTY_API_ENTRY = () => ({
  id: Date.now().toString() + Math.random().toString(36).slice(2),
  whenToRun: "",
  apiUrl: "",
  method: "POST",
  headers: {},
  formConfig: {
    fields: [],
    lineItemsConfig: { catalogArrayPath: "allProducts", targetPath: "line_items", columns: [] },
  },
});

export const EMPTY_ACTION_CONFIGS = {
  button_message: { whenToRun: "", body: "", buttons: "" },
  // catalogId/catalogName are filled in by the catalog picker in ActionConfigPanel — this is
  // the catalog the AI sends whenever a catalog_send intent action fires. A specific intent's
  // own action config can still override these per-intent (see responseService.js
  // resolveIntentActions). products holds the ENTIRE catalog (up to Meta's ~100-per-fetch
  // cap); responseService.js search-narrows it to whatever the customer asked for, capped at
  // WhatsApp's 30-product message limit.
  catalog_send: { whenToRun: "", message: "", catalogId: "", catalogName: "", products: [] },
  api_integration: { entries: [] },
  assignment: { whenToRun: "", assigneeId: "", assigneeType: "", assigneeName: "" },
  close_conversation: { whenToRun: "" },
  // No image URL here — the image is never uploaded/generated by the admin, it's
  // resolved at send time from whatever image link already appears in the matched
  // knowledge source content (see responseService.js buildImageStructuredMessage).
  image_message: { whenToRun: "", caption: "" },
};

export const DAYS_OF_WEEK = [
  { day: 1, label: "Monday" },
  { day: 2, label: "Tuesday" },
  { day: 3, label: "Wednesday" },
  { day: 4, label: "Thursday" },
  { day: 5, label: "Friday" },
  { day: 6, label: "Saturday" },
  { day: 0, label: "Sunday" },
];

export const DEFAULT_SCHEDULE = DAYS_OF_WEEK.map(({ day }) => ({
  day,
  active: day >= 1 && day <= 5,
  from: "09:00",
  to: "17:00",
}));

export const TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Dhaka",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Australia/Sydney",
  "Pacific/Auckland",
  "Africa/Cairo",
  "Africa/Nairobi",
];

export const EMPTY_OFFICE_HOURS = {
  enabled: false,
  timezone: "UTC",
  schedule: DEFAULT_SCHEDULE,
};

export const EMPTY_CONTACT_FILTER = {
  filterType: "all",
  specificContacts: [],
  countryCodes: [],
  labels: [],
};

// Keep in sync with server/models/AgentModel.js RESPONSE_FORMATS — the four wire
// formats a reply can go out as; "text" is always feasible, the others depend on
// the channel and how many options a given reply has (see responsePolicy.service.js).
export const RESPONSE_FORMATS = [
  { value: "text", label: "Plain text" },
  { value: "list", label: "List message" },
  { value: "buttons", label: "Quick-reply buttons" },
  { value: "catalog", label: "Catalog message" },
];

export const EMPTY_RESPONSE_PREFERENCES = {
  defaultFormat: "text",
  allowedFormats: RESPONSE_FORMATS.map((f) => f.value),
  bullets: true,
  listStyle: "bullet",
  autoFormatNonTaskReplies: false,
};

// How options are rendered when a reply downgrades to plain text (see
// server/services/aiService/semanticPlanner.service.js formatOptionsAsText()).
export const LIST_STYLES = [
  { value: "bullet", label: "Bullet points" },
  { value: "numbered", label: "Numbered list" },
  { value: "flowing", label: "Flowing text (no list)" },
];

export const EMPTY_AGENT = {
  name: "",
  instructions: {
    role: "",
    context: "",
    communicationStyle: "",
    tone: "",
    boundaries: "",
    flow: "",
    businessRules: "",
  },
  intents: [],
  knowledgeSourceIds: [],
  fallbackMessage:
    "I don't have enough information to answer that. A team member will help you shortly.",
  enabledActions: [],
  actionConfigs: { ...EMPTY_ACTION_CONFIGS },
  officeHours: { ...EMPTY_OFFICE_HOURS, schedule: DEFAULT_SCHEDULE.map((d) => ({ ...d })) },
  contactFilter: { ...EMPTY_CONTACT_FILTER },
  useStructuredPlanner: true,
  responsePreferences: { ...EMPTY_RESPONSE_PREFERENCES },
  isDefault: false,
  isActive: true,
};

export const INSTRUCTION_FIELDS = [
  { key: "role", label: "Role", hint: "e.g. You are a helpful e-commerce assistant." },
  { key: "context", label: "Context", hint: "Business background the agent should know." },
  { key: "communicationStyle", label: "Communication Style", hint: "e.g. Formal, casual, concise." },
  { key: "tone", label: "Tone", hint: "e.g. Friendly, professional, empathetic." },
  { key: "boundaries", label: "Boundaries", hint: "Topics the agent must never address." },
  { key: "flow", label: "Conversation Flow", hint: "How the agent should guide conversations." },
  { key: "businessRules", label: "Business Rules", hint: "Pricing rules, escalation triggers, etc." },
];

export const SOURCE_TYPE_LABELS = { pdf: "PDF", docx: "DOCX", txt: "TXT", faq: "FAQ", url: "URL", xlsx: "Excel" };

// `description` added (Rahim, 2026-09-03: "add icons and sub para just
// like we did for the config") — each one now renders as a proper
// SectionCard, same as Instructions/Knowledge Sources/Message Format on
// the Config step, instead of a bare label + toggle row.
export const ACTION_TOGGLE_GROUPS = [
  { label: "Enable button message", types: ["button_message"], configKey: "button_message",
    description: "Sends up to 3 tappable quick-reply buttons instead of a plain-text list." },
  { label: "Enable catalog message", types: ["catalog_send"], configKey: "catalog_send",
    description: "Searches a connected product catalog and sends only what matches the customer's ask." },
  { label: "Enable API integration", types: ["api_integration"], configKey: "api_integration",
    description: "Calls an external API — order status, booking lookups, or your own backend." },
  { label: "Enable image message", types: ["image_message"], configKey: "image_message",
    description: "Sends an image found in a matched knowledge source, with an optional caption." },
  { label: "Assign conversation", types: ["assignment"], configKey: "assignment",
    description: "Hands the conversation off to a specific team member or team." },
  { label: "Close conversation", types: ["close_conversation"], configKey: "close_conversation",
    description: "Marks the conversation resolved once the agent's job here is done." },
];

export const ACCEPTED_MIME = ".pdf,.docx,.doc,.txt,.md,.xlsx,.xls";

export const CONTACT_FILTER_OPTIONS = [
  { value: "all",               label: "All Contacts" },
  { value: "specific_contacts", label: "Specific Contacts" },
  { value: "country_code",      label: "Country Code" },
  { value: "label",             label: "Label" },
];
